// A fee line added by product-options.js is locked to the line it came with:
// same quantity, and it leaves when that line leaves. Rather than hooking every
// way a cart can change, this reconciles the whole cart after each update, so a
// cart edited from anywhere still ends up consistent.
//
// Lines added in one submit share properties._set. The fee lines also carry
// _fee and _fee_per, the number of fields charging that fee, so the target
// quantity is parent.quantity * _fee_per.
//
// A cart with no fee lines must cost nothing beyond the one read of /cart.js:
// no write, no re-render, nothing for the great majority of carts that never
// contain a fee line at all.

(() => {
  // Guards re-entry across the whole asynchronous span of a reconcile, not just
  // its synchronous head: set before the first await and cleared only in the
  // finally, which runs after the last one. The write below deliberately
  // publishes no cartUpdate, so this cannot feed itself, but a genuine update
  // arriving from elsewhere mid-flight would otherwise act on a cart that is
  // already being rewritten.
  let reconciling = false;

  // A cart update that lands mid-flight is remembered rather than dropped. Dawn
  // re-enables its quantity controls after it publishes, so a second click on
  // the plus button can arrive while the first reconcile is still reading, and
  // without this the fee would stay behind the product for the rest of the
  // session. The finally re-runs once for it.
  //
  // This is a queue of at most one, not a retry: only a call that was *refused*
  // sets it, and a call that actually runs is by definition not refused, so a
  // re-run can never queue itself. Every re-run therefore has to be paid for by
  // a fresh external event, and the flag is cleared before the re-run starts, so
  // N events can cause at most N runs. A failed write in particular does not
  // queue anything, which keeps ruling 3 intact.
  let dirty = false;

  // The elements to re-render through. cart-drawer is deliberately not one of
  // them: its renderContents() opens the drawer, and a reconcile must never do
  // that. Both of these extend CartItems, so both expose getSectionsToRender()
  // and getSectionInnerHTML().
  //
  // All present elements are used, not the first one found: with the drawer
  // enabled the drawer is on every page, so the cart page has both, and
  // rendering only one of them would leave the other showing stale quantities.
  function cartElements() {
    return Array.from(document.querySelectorAll('cart-items, cart-drawer-items'));
  }

  // CartItems.getSectionsToRender() reads ids that only exist alongside the cart
  // page sections, so it can throw where the element is present but those
  // wrappers are not. A element we cannot plan for is simply not rendered.
  function sectionsFor(element) {
    try {
      return element.getSectionsToRender() || [];
    } catch (error) {
      return [];
    }
  }

  // Fee line keys mapped to the quantity they should have. A line with no _set
  // token belongs to no group and is never touched, which is every line in an
  // ordinary cart. An orphaned fee, whose parent line is gone, goes to zero,
  // which is how /cart/update removes it.
  function quantityCorrections(cart) {
    const groups = new Map();

    (cart.items || []).forEach((item) => {
      const token = item.properties && item.properties._set;
      if (!token) return;

      const group = groups.get(token) || { parent: null, fees: [] };
      // Compared against the string, the same test the cart markup uses to
      // decide which lines get a read-only quantity, so the two never disagree
      // about which line is a fee.
      if (String(item.properties._fee) === 'true') group.fees.push(item);
      else group.parent = item;
      groups.set(token, group);
    });

    const updates = {};
    groups.forEach((group) => {
      group.fees.forEach((fee) => {
        // A _fee_per that is missing, unparseable or not a count of fields
        // counts as one field charging the fee. Tracking the parent at the wrong
        // multiple beats not tracking it at all.
        const declared = parseInt(fee.properties._fee_per, 10);
        const per = Number.isInteger(declared) && declared > 0 ? declared : 1;
        const target = group.parent ? group.parent.quantity * per : 0;
        if (fee.quantity !== target) updates[fee.key] = target;
      });
    });

    return updates;
  }

  // Where the keyboard was before a render, so it can be put back after: the
  // replaced markup is a fresh copy from the server, so the node that had focus
  // no longer exists. Mirrors how Dawn refinds a control inside its line item.
  function focusedControl() {
    const active = document.activeElement;
    if (!active || !active.closest || !active.getAttribute) return null;

    const name = active.getAttribute('name');
    const item = active.closest('.cart-item');
    if (!name || !item || !item.id) return null;

    return { name: name, itemId: item.id };
  }

  function restoreFocus(spot) {
    if (!spot) return;

    const item = document.getElementById(spot.itemId);
    const control = item && item.querySelector(`[name="${spot.name}"]`);
    if (!control) return;

    // Replacing the drawer's contents a second time invalidates the list of
    // focusable nodes that Dawn's trapFocus captured when the drawer opened, so
    // Tab could walk out of an open dialog. Re-trapping rebuilds that list,
    // which is what Dawn itself does after its own quantity change. The class
    // check keeps a closed drawer, which is present on every page, from
    // swallowing focus away from the cart page behind it.
    const drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.classList.contains('active') && typeof trapFocus === 'function') {
      trapFocus(drawer, control);
      return;
    }

    control.focus();
  }

  // Pulls a section's inner markup out of a section render, the way CartItems
  // does. Used for the header count fallback below, where there is no cart
  // element to borrow the method from.
  function sectionInnerHTML(html, selector) {
    const found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return found ? found.innerHTML : null;
  }

  // Replaces each planned section, skipping any target an earlier plan already
  // wrote: the cart icon bubble is in both plans and needs writing once.
  function renderSections(plans, sections) {
    const written = new Set();

    plans.forEach((plan) => {
      plan.sections.forEach((section) => {
        const html = sections[section.section];
        if (!html) return;

        const container = document.getElementById(section.id);
        if (!container) return;

        const target = (section.selector && container.querySelector(section.selector)) || container;
        if (written.has(target)) return;

        // Claimed only once the write succeeded, so a plan whose extraction
        // throws does not lock a later plan out of the same target.
        try {
          const markup = plan.render(html, section.selector);
          if (typeof markup !== 'string') return;
          target.innerHTML = markup;
          written.add(target);
        } catch (error) {
          console.error(error);
        }
      });
    });
  }

  // Removing an orphaned fee can empty the cart, and the empty wording is
  // revealed by a class rather than by the section markup. Toggled the same way
  // and on the same elements as Dawn does after its own quantity change.
  function applyEmptyState(cart) {
    const empty = cart.item_count === 0;

    document.querySelectorAll('cart-items, cart-drawer-items, cart-drawer').forEach((element) => {
      element.classList.toggle('is-empty', empty);
    });

    const footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', empty);
  }

  async function reconcile() {
    if (reconciling) {
      dirty = true;
      return;
    }
    reconciling = true;

    try {
      const cartResponse = await fetch(`${window.routes.cart_url}.js`, { cache: 'no-store' });
      // Checked before parsing, so a 5xx that returns an HTML error page is
      // logged as what it is rather than as an unexplained JSON parse failure.
      if (!cartResponse.ok) {
        console.error(`cart-line-groups: could not read the cart (${cartResponse.status})`);
        return;
      }
      const cart = await cartResponse.json();

      const updates = quantityCorrections(cart);
      if (Object.keys(updates).length === 0) return;

      const plans = cartElements().map((element) => ({
        render: (html, selector) => element.getSectionInnerHTML(html, selector),
        sections: sectionsFor(element),
      }));

      const names = [];
      plans.forEach((plan) => {
        plan.sections.forEach((section) => {
          if (section.section && names.indexOf(section.section) === -1) names.push(section.section);
        });
      });

      // Nothing on this page can be re-rendered: either it carries no cart
      // element, which is every page but the cart page when the cart is a page
      // rather than a drawer, or the elements it does carry could not be planned
      // for. Either way the header count still changes, so that one section is
      // refreshed on its own. Gated on the plans yielding no sections rather
      // than on there being no elements, so a page whose elements all failed to
      // plan is covered too.
      if (names.length === 0) {
        plans.length = 0;
        plans.push({
          render: sectionInnerHTML,
          sections: [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }],
        });
        names.push('cart-icon-bubble');
      }

      const body = {
        updates: updates,
        sections: names,
        sections_url: window.location.pathname,
      };

      const response = await fetch(window.routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify(body),
      });
      const parsed = await response.json();

      // A failed correction is left alone and logged. A wrong fee quantity is
      // recoverable on the next update; a retry loop is not.
      if (!response.ok || parsed.status) {
        console.error('cart-line-groups: could not correct fee quantities', parsed);
        return;
      }

      const spot = focusedControl();
      if (parsed.sections) renderSections(plans, parsed.sections);
      applyEmptyState(parsed);
      restoreFocus(spot);
    } catch (error) {
      console.error(error);
    } finally {
      reconciling = false;

      // One queued re-run for the update that arrived while this one was in
      // flight. Cleared before the re-run, so the re-run starts with an empty
      // queue and can only be queued again by another external event.
      if (dirty) {
        dirty = false;
        reconcile();
      }
    }
  }

  // The load-time passes never queue. If a reconcile is already in flight it is
  // reading a cart at least as fresh as this moment, so a second pass would find
  // the same thing. Only a cartUpdate is worth queueing, because it reports a
  // change that may have landed after the in-flight read.
  function heal() {
    if (reconciling) return;
    reconcile();
  }

  // Heals a cart changed by some path that published nothing here, another tab
  // included. It runs only where a stale quantity is actually on screen, so an
  // ordinary page view is charged nothing: the cart page, and the moment the
  // drawer opens. Checkout is reachable only through one of those two, so no
  // buyer can reach payment past an unhealed cart.
  function watchWhereVisible() {
    // The cart page needs no new markup to recognise: its section is the only
    // thing that renders a cart-items element.
    if (document.querySelector('cart-items')) heal();

    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    let wasActive = drawer.classList.contains('active');
    if (wasActive) heal();

    // CartDrawer.open() publishes no event and exposes no hook; it adds `active`
    // to itself, so that class is the only honest signal that the drawer is now
    // on screen. Watched rather than hooked to the cart icon because the drawer
    // is also opened by add to cart, by the quick add buttons and by anything
    // else that calls open(), all of which a click listener on the icon would
    // miss, and because that listener would also fire when the icon is clicked
    // with the drawer already open.
    const observer = new MutationObserver(() => {
      const isActive = drawer.classList.contains('active');
      if (isActive && !wasActive) heal();
      wasActive = isActive;
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  // Loaded from layout/theme.liquid, so once per page. The flag makes a second
  // tag added later harmless rather than a doubled subscription.
  if (!window.cartLineGroupsReady) {
    window.cartLineGroupsReady = true;

    // Fires everywhere, on every cart change, from the cart page and the drawer
    // alike, because CartDrawerItems extends CartItems and both publish it.
    subscribe(PUB_SUB_EVENTS.cartUpdate, reconcile);

    // Deferred scripts run before this event, but the readyState check keeps the
    // watchers from being lost if that ever stops being true.
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchWhereVisible);
    else watchWhereVisible();
  }
})();
